import { useEffect, useRef, useState } from 'react'

// The console-feed mechanics shared by the landing code console and the
// webhooks signed-out card: seed two rows immediately so the feed never
// renders empty, append one on an interval, and cap retention so an afternoon
// in a background tab doesn't grow the DOM. Screens supply only their
// event-specific metadata via `makeEvent`.

const FEED_CAP = 12
const FEED_INTERVAL_MS = 2100

/**
 * @param makeEvent Builds the event for a given sequence index. Must be
 * referentially stable (module-level, or `useCallback`) — it is an effect
 * dependency, so a new identity per render would restart the feed.
 */
export const useCyclingFeed = <T>(
  makeEvent: (index: number) => T
): (T & { id: number })[] => {
  const [events, setEvents] = useState<(T & { id: number })[]>([])
  const counter = useRef(0)

  useEffect(() => {
    const push = () => {
      const event = { ...makeEvent(counter.current), id: counter.current }
      counter.current += 1
      setEvents(previous => [...previous, event].slice(-FEED_CAP))
    }

    push()
    push()
    const timer = setInterval(push, FEED_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [makeEvent])

  return events
}
